import jsPDF from "jspdf";
import autoTable, { RowInput } from "jspdf-autotable";
import orderServices from "../../services/orders/orderservice";

interface LineItem {
  productId: number;
  productName: string;
  categoryId: number;
  categoryName: string;
  hsnCode: string;
  quantity: number;
  taxableAmount: number;
  taxAmount: number;
  grossAmount: number;
  qtyUnit: string;
  gst: number;
  createdDate: string;
  ewayBillNo: string | null;
  vehicleNo: string | null;
  challanNo?: number;
}

interface CategorySummary {
  categoryId: number;
  categoryName: string;
  totalQuantity: number;
  totalTaxableAmount: number;
  totalTaxAmount: number;
  totalGrossAmount: number;
}

interface VesselDetail {
  productName: string;
  totalQuantity: number;
  vessels_5L: number;
  vessels_3L: number;
  vessels_2L: number;
}

interface ChallanDetails {
  challanNo: number;
  challanType: string;
  generatedBy: string;
  fromAddress: string;
  toAddress: string;
  lineItems: LineItem[];
  categorySummaries: CategorySummary[];
  totalCount: number;
  hasEwayBill: boolean;
  vehicleNoFromDelivery: string;
  vesselDetails: VesselDetail[];
}

const formatDate = (dateString?: string) => {
  const date = dateString ? new Date(dateString) : new Date();
  return isNaN(date.getTime())
    ? "-"
    : date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

export const generateChallanDetailsPDF = async (challanNo: number) => {
  try {
     const challan: ChallanDetails = await orderServices.GetChallanDetailsByNo(challanNo);

    if (!challan) {
      console.error("No challan details found");
      return;
    }

    // ✅ Vessel details directly from challan
    const vesselDetailsData = challan.vesselDetails ?? [];

    const validLineItems = challan.lineItems.filter((x) => Number(x.grossAmount) >= 0);
    const validCategorySummaries = challan.categorySummaries.filter(
      (x) => Number(x.totalGrossAmount) >= 0
    );

    const priceValueTotal = validCategorySummaries.reduce(
      (sum, c) => sum + (c.totalTaxableAmount || 0),
      0
    );
    const taxValueTotal = validCategorySummaries.reduce(
      (sum, c) => sum + (c.totalTaxAmount || 0),
      0
    );
    const grandTotal = validCategorySummaries.reduce(
      (sum, c) => sum + (c.totalGrossAmount || 0),
      0
    );

    const doc = new jsPDF("p", "mm", "a4");
    let yPos = 15;
    const leftCol = 14;
    const rightCol = 120;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`${challan.challanType} Details`, leftCol, yPos);
    yPos += 8;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");

    // --- Addresses ---
    // --- FROM SECTION ---
    doc.setFont("helvetica", "bold");
    doc.text("From:", leftCol, yPos);
    doc.text("Desiri Naturals Private Limited", leftCol, yPos + 6);
    doc.setFont("helvetica", "normal");
    const fromLines = doc.splitTextToSize(challan.fromAddress || "Not available", 80);
    doc.text(fromLines, leftCol, yPos + 12);
    doc.text("GST #: 29AAGCD5864J1Z4", leftCol, yPos + 25);

    // --- TO SECTION ---
    doc.setFont("helvetica", "bold");
    doc.text("To:", rightCol, yPos);
    doc.text( "Desiri Naturals Private Limited", rightCol, yPos + 6);
    doc.setFont("helvetica", "normal");
    const toLines = doc.splitTextToSize(challan.toAddress || "Not available", 80);
    doc.text(toLines, rightCol, yPos + 12);
    doc.text("GST #: 29AAGCD5864J1Z4", rightCol, yPos + 30);

    yPos += Math.max(fromLines.length, toLines.length) * 7 + 10;

    // Purpose
    doc.setFont("helvetica", 'bold');
    doc.text('Purpose of material movement:', leftCol, yPos);
    doc.setFont("helvetica", 'normal');
    yPos += 6;
    doc.setFontSize(10);
    doc.text('1. Below list of materials are moved against the customer orders placed on Desiri Naturals Pvt Ltd through online.', leftCol, yPos, { maxWidth: 180 });
    yPos += 6;
    doc.text('2. Invoice will be generated after physical delivery to customer address mentioned below.', leftCol, yPos, { maxWidth: 180 });
    yPos += 10;

    // --- Challan Info ---
    doc.setFont("helvetica", "bold");
    doc.text(`Challan # : ${challan.challanNo}`, leftCol, yPos);
    doc.text(`Eway Bill # : ${challan.hasEwayBill ? challan.lineItems[0]?.ewayBillNo || "-" : "-"}`, rightCol,yPos);    
    yPos += 6;
    doc.text(`Vehicle # : ${challan.hasEwayBill ? challan.lineItems[0]?.vehicleNo || "-": challan.vehicleNoFromDelivery}`, leftCol,yPos);
    doc.text(`Generated By : ${challan.generatedBy}`, rightCol, yPos);
    yPos += 6;
    doc.text(`Generated Date : ${formatDate(challan.lineItems[0]?.createdDate)}`,leftCol,yPos);
    yPos += 10;

    // --- Product Details ---
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Product Details", leftCol, yPos);
    doc.setFont("helvetica", "normal");
    yPos += 6;

    if (validLineItems.length > 0) {
      const grouped: { [key: string]: LineItem[] } = {};
      validLineItems.forEach((item) => {
        if (!grouped[item.categoryName]) grouped[item.categoryName] = [];
        grouped[item.categoryName].push(item);
      });

      const body: RowInput[] = [];

      Object.entries(grouped).forEach(([category, items]) => {
        // Header for category
        body.push([
          {
            content: category,
            colSpan: 6,
            styles: { fontStyle: "bold", textColor: [0, 0, 0], halign: "left" },
          },
        ]);

        // Products
        items.forEach((item) => {
          body.push([
            item.productName,
            item.quantity.toString(),
            item.qtyUnit,
            item.hsnCode,
            item.taxableAmount.toFixed(2),
            item.taxAmount.toFixed(2),
            item.grossAmount.toFixed(2),
          ]);
        });

        const catSummary = validCategorySummaries.find(
          (x) => x.categoryName === category
        );
        body.push([
        { 
          content: `${category} Total: ${catSummary?.totalQuantity || 0} items (Rs.${(catSummary?.totalGrossAmount || 0).toFixed(2)})`, 
          colSpan: 6, 
          styles: { 
            fontStyle: 'bold',
            textColor: [0, 0, 0]
          }
        }
      ]);
      });

      autoTable(doc, {
            head: [['Product', 'Quantity', 'HSN', 'Price Value(Rs)', 'Tax Value(Rs)', 'Total Amount(Rs)']],
            body: body,
            startY: yPos,
            headStyles: {
              fontStyle: 'bold'
            },
            columnStyles: {
              0: { cellWidth: 'auto', halign: 'left' },
              1: { cellWidth: 'auto', halign: 'center' },
              2: { cellWidth: 'auto', halign: 'center' },
              3: { cellWidth: 'auto', halign: 'center' },
              4: { cellWidth: 'auto', halign: 'center' },
              5: { cellWidth: 'auto', halign: 'center' }
            },
            styles: {
              cellPadding: 3,
              fontSize: 10,
              valign: 'middle',
              font: 'helvetica'
            },
            didDrawPage: (data) => {
              yPos = data.cursor?.y || yPos + 10;
            }
          });
        } else {
          doc.text('No products found in the challan.', leftCol, yPos);
          yPos += 10;
        }
      

    // --- Summary Totals ---
     yPos += 10;
    doc.setFontSize(14);
    doc.setFont("helvetica", 'bold');
    doc.text('Summary Totals', leftCol, yPos);
    doc.setFont("helvetica", 'normal');
    yPos += 8;

    const summaryBody: RowInput[] = validCategorySummaries.map((c) => [
      c.categoryName,
      c.totalTaxableAmount.toFixed(2),
      c.totalTaxAmount.toFixed(2),
      c.totalGrossAmount.toFixed(2),
    ]);

    summaryBody.push([
      {
        content: "Total",
        styles: { fontStyle: "bold", textColor: [0, 0, 255] },
      },
      {
        content: priceValueTotal.toFixed(2),
        styles: { fontStyle: "bold", textColor: [0, 0, 255] },
      },
      {
        content: taxValueTotal.toFixed(2),
        styles: { fontStyle: "bold", textColor: [0, 0, 255] },
      },
      {
        content: grandTotal.toFixed(2),
        styles: { fontStyle: "bold", textColor: [0, 0, 255] },
      },
    ]);

    autoTable(doc, {
      head: [["Category", "Price Value (Rs)", "Tax (Rs)", "Total (Rs)"]],
      body: summaryBody,
      startY: yPos,
      headStyles: {
      textColor: 255,
      fontStyle: 'bold'
    },
      columnStyles: {
      0: { cellWidth: 'auto', halign: 'left' },
      1: { cellWidth: 'auto', halign: 'center' },
      2: { cellWidth: 'auto', halign: 'center' },
      3: { cellWidth: 'auto', halign: 'center' }
    },
    bodyStyles: {
      [summaryBody.length - 1]: { fontStyle: 'bold', textColor: [0, 0, 255] }
    },
    styles: {
      cellPadding: 3,
      fontSize: 10,
      valign: 'middle'
    },
    didDrawPage: (data) => {
      yPos = data.cursor?.y || yPos + 10;
    }
  });

  // --- Vessel Details Section ---
    if (vesselDetailsData.length > 0) {
      yPos += 15;
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Vessel Details", leftCol, yPos);
      doc.setFont("helvetica", "normal");
      yPos += 8;

      const vesselBody: RowInput[] = vesselDetailsData.map((v) => [
        v.productName,
        v.totalQuantity?.toString() || "0",
        v.vessels_5L?.toString() || "0",
        v.vessels_3L?.toString() || "0",
        v.vessels_2L?.toString() || "0",
      ]);

      autoTable(doc, {
        head: [["Name", "Total Qty", "5L Vessels", "3L Vessels", "2L Vessels"]],
        body: vesselBody,
        startY: yPos,
        headStyles: { fontStyle: "bold" },
        styles: { cellPadding: 3, fontSize: 10, valign: "middle" },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "center" },
          2: { halign: "center" },
          3: { halign: "center" },
          4: { halign: "center" },
        },
        didDrawPage: (data) => {
          yPos = data.cursor?.y || yPos + 10;
        },
      });
    }

    doc.save(`Challan-${challan.challanNo}.pdf`);
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};
